/**
 * Applies a named palette to the document.
 *
 * ## Why this writes inline styles on `<html>`
 *
 * Every theme token is declared on `:root` — light in `:root {}`, dark in the
 * `@variant dark` block nested inside it, which compiles to
 * `:root:is(.dark, .dark *)`. Both select `<html>`. An inline style on that
 * element outranks both, which is what lets a palette override the shipped
 * values without a stylesheet rebuild.
 *
 * ## Why it must be appearance-aware
 *
 * That same fact is a trap. An inline value beats the light rule *and* the dark
 * rule, unconditionally, and has no way to vary by `.dark`. So a palette
 * applied once would freeze every token it touched across both appearances:
 * toggling to dark would flip every un-themed token and leave the themed ones
 * where they were. The UI would be half-dark, and the cause would be three
 * layers from the symptom.
 *
 * So the applier is a function of *both* axes — palette and appearance — and
 * re-runs when either changes. `applyThemePalette` writes the matching half.
 * `themePalette.browser.tsx` asserts a themed token actually *differs* between
 * appearances, which is the only assertion that can tell "the theme applied"
 * apart from "the theme applied and then froze".
 *
 * @module
 */
import {
  DEFAULT_THEME_ID,
  findThemePalette,
  THEME_ROLE_VARIABLES,
  THEME_ROLES,
  type ThemePalette,
} from "@cmmd-forge/contracts/theme";

import { getCustomTheme } from "./themeLibrary";

export type ThemeAppearance = "light" | "dark";

/** Set on `<html>` so CSS and tests can see which palette is live. */
const THEME_ID_DATA_ATTRIBUTE = "themeId";

/**
 * Boot cache for the pre-paint shell in `index.html`.
 *
 * That shell runs before any module loads, so it cannot import the palette
 * table — and without the canvas colour it paints the shipped background and
 * then swaps, which is a full-page flash on every cold start with a theme on.
 * Rather than duplicate the palettes into the HTML, where they would drift, the
 * applier leaves behind just the two values the shell needs.
 */
const THEME_BOOT_STORAGE_KEY = "forge:theme-boot:v1";

function writeBootCache(background: string, appearance: ThemeAppearance): void {
  try {
    window.localStorage.setItem(THEME_BOOT_STORAGE_KEY, JSON.stringify({ appearance, background }));
  } catch {
    // Private mode, quota, disabled storage. A flash on cold boot is the cost;
    // it must never break applying the theme.
  }
}

function clearBootCache(): void {
  try {
    window.localStorage.removeItem(THEME_BOOT_STORAGE_KEY);
  } catch {
    // See writeBootCache.
  }
}

function rootElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.documentElement;
}

/**
 * Removes every variable this module owns.
 *
 * Only the roles are cleared, never the whole `style` attribute: other code
 * legitimately writes inline custom properties to `<html>` (the composer's
 * measured heights, the window-controls insets), and wiping the attribute would
 * take those with it.
 */
export function clearThemePalette(): void {
  const root = rootElement();
  if (root === null) return;

  for (const role of THEME_ROLES) {
    root.style.removeProperty(THEME_ROLE_VARIABLES[role]);
  }
  delete root.dataset[THEME_ID_DATA_ATTRIBUTE];
  clearBootCache();
}

/** Writes the half of `palette` matching `appearance`. */
export function applyThemePalette(palette: ThemePalette, appearance: ThemeAppearance): void {
  const root = rootElement();
  if (root === null) return;

  const colors = appearance === "dark" ? palette.dark : palette.light;
  for (const role of THEME_ROLES) {
    const value = colors[role];
    // A palette may have no opinion on a role. Skipping rather than writing an
    // empty string is what lets it fall through to the shipped token instead of
    // resolving to nothing.
    if (value === undefined) {
      root.style.removeProperty(THEME_ROLE_VARIABLES[role]);
      continue;
    }
    root.style.setProperty(THEME_ROLE_VARIABLES[role], value);
  }
  root.dataset[THEME_ID_DATA_ATTRIBUTE] = palette.id;
  if (colors.background !== undefined) writeBootCache(colors.background, appearance);
}

/**
 * The single entry point: resolves an id and applies or clears accordingly.
 *
 * `forge` is not a palette, it is the absence of one — it clears, so the app
 * renders exactly what `index.css` declares. An unknown id does the same rather
 * than throwing, because a settings file naming a removed palette should cost
 * the user their theme and nothing else.
 */
export function resolveAndApplyTheme(themeId: string, appearance: ThemeAppearance): void {
  if (themeId === DEFAULT_THEME_ID) {
    clearThemePalette();
    return;
  }

  // Built-ins first, then the user's installed library: a custom id resolves
  // exactly once, and a theme the user installed always paints.
  const palette = findThemePalette(themeId) ?? getCustomTheme(themeId);
  if (palette === undefined) {
    clearThemePalette();
    return;
  }

  applyThemePalette(palette, appearance);
}
